import React, {useContext, useMemo, useRef, useState} from "react";
import PermissionEnum from "../../controllers/PermissionEnum";
import PaginationRequestSearch from "../../controllers/PaginationRequestSearch";
import {AxiosResponse} from "axios";
import PaginationResponse from "../../controllers/PaginationResponse";
import SubmissionDisplay from "../../controllers/SubmissionDisplay";
import AppContext from "../../appContext";
import PreReportData from "../../controllers/PreReportData";
import Data from "../../controllers/Data";
import PagedSearchTable, {PagedTableFunctions, TableColumn} from "../../components/PagedSearchTable";
import {useStateSetterArray} from "../../immutableState";
import File from "../../controllers/File";
import WarningPopup, {useWarningState} from "../../components/WarningPopup";
import {classNames, wrapLoader} from "../../wrapper";
import Lab from "../../controllers/Lab";
import {dateFormat} from "../../date";
import Dialog from "../../components/Dialog";
import {DownloadSvg, FileSvg, LabResult, Print} from "../Submissions";
import SubmissionController from "../../controllers/SubmissionController";
import CustomSnack from "../../components/CustomSnack";
import MfwSuccess from "../../controllers/MfwSuccess";
import MFWSuccessDialog from "../../components/MFWSuccessDialog";


const CompletedResultsSoil: React.FC<{
    canPost: boolean,
    archived: boolean,
    call: (request: PaginationRequestSearch) => Promise<AxiosResponse<PaginationResponse<SubmissionDisplay>, any>>
}> = (props) => {
    const context = useContext(AppContext);

    const [printData, setPrintData] = useState<number | null>(null);
    const [resultData, setResultData] = useState<Data[] | null>(null);
    const pagedTableRef = useRef<PagedTableFunctions<SubmissionDisplay>>()
    const [selectedSubmissions, setSelectedSubmissions, updateSelectedSubmissions] = useStateSetterArray<number>([])
    const [id, setId] = useState(0);
    const [files, setFiles] = useState<File[]>([])
    const [successData, setSuccessData] = useState<MfwSuccess[] | null>(null);
    
    const warningStateArchive = useWarningState<number | null>(null);
    const warningStateRestore = useWarningState<number | null>(null);

    function results(row: SubmissionDisplay) {
        setId(row.id);
        wrapLoader(context, SubmissionController.getResults({ id: row.id }), data => {
            setResultData(data);
        })
    }

    function print(row: SubmissionDisplay) {
        setId(row.id);
        setPrintData(row.id);
    }

    function getDocks(row: SubmissionDisplay) {
        const hideLoader = context.showLoader();
        SubmissionController.getDocs({id: row.id})
            .then(resp => {
                if (resp.data.length == 0)
                    context.showSnack(<CustomSnack svg={<FileSvg/>} title={"No files found."} />)
                setFiles(resp.data)
            })
            .finally(hideLoader)
    }

    const options = (isManual: boolean) => {
        return isManual
            ? Object.entries({
                docs: "docs"
            })
            : Object.entries({
                print: "print",
                docs: "docs"
            })
    }

    function selectOption(event: React.ChangeEvent<HTMLSelectElement>, item: SubmissionDisplay) {
        const value = event.target.value;
        if (value == "print") {
            print(item)
        } else if (value == "docs") {
            getDocks(item)
        }
    }
    function showWarningArchive(id: number) {
        warningStateArchive.show(`Are you sure you want to archive the submission with id: ${id}? `, id)
    }
    function showWarningRestore(id: number) {
        warningStateRestore.show(`Are you sure you want to restore the submission with id: ${id}? `, id)
    }
    function archive(id: number | null) {
        if (id == null) return;
        wrapLoader(context, SubmissionController.archive({ id: id }), () => {
            pagedTableRef.current?.refresh()
        })
    }

    function restore(id: number | null) {
        if (id == null) return;
        wrapLoader(context, SubmissionController.restore({ id: id }), () => {
            pagedTableRef.current?.refresh()
        })
    }

    const getActions = (item: SubmissionDisplay) => <div className="flex justify-end">

        <div className="btn-primary btn-sm " onClick={() => results(item)}>
            results
        </div>
        {
            props.archived
                ? <div className={classNames("btn-primary btn-sm")} onClick={() => showWarningRestore(item.id)}>
                    restore
                </div>
                : context.hasPermission(PermissionEnum.SubmissionsArchive)
                    ? <div className={classNames("btn-error btn-sm")} onClick={() => showWarningArchive(item.id)}>
                        archive
                    </div>
                    : null
        }
        <select value={'empty'}
                className="bg-center bg-no-repeat outline-none btn-primary btn-sm p-2 w-9 "
                style={{
                    appearance: "none",
                    backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke-width=\'2\' stroke=\'white\' class=\'w-6 h-6\'><path stroke-linecap=\'round\' stroke-linejoin=\'round\' d=\'M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5\'/></svg>")'
                }}
                onChange={e => selectOption(e, item)}>
            <option value="empty" className="hidden" disabled={true}></option>
            {options(item.lab == Lab.ManualSoil).map(([text, value]) => <option key={value} value={value}>{text}</option>)}
        </select>
    </div>

    const tableData = useMemo(() => {
        let tableData: TableColumn<SubmissionDisplay>[] = [
            {
                header: 'Id',
                row: item => item.id
            },
            {
                header: 'Client (Farm)',
                row: item => <span>{item.clientName} ({item.farmName})</span>
            },
            {
                header: 'Samples',
                row: item => <span>{item.items}</span>
            },
            {
                header: 'Lab',
                row: item => <span>{item.lab}</span>
            },
            {
                header: 'Submitted On / Completed On',
                row: item => <span>{dateFormat(item.submitted) || "_"} / {dateFormat(item.completed)}</span>
            },
            {
                header: 'Prepared By',
                row: item => <span>{item.submittedBy}</span>
            }]

        if (props.canPost) {
            tableData.push(
                {
                    header: 'Date Posted To mfw',
                    row: item => <span>{dateFormat(item.postedToMfwDate, '%d %M %Y %H:%i') || "_"}</span>
                })
        }

        if (props.archived) {
            tableData.push({
                header: 'Archived By',
                row: item => <>
                    {item.archivedBy}
                    <div className="text-xs text-gray-500">{dateFormat(item.archivedDate)}</div>
                </>
            })
        }

        tableData.push({
            header: <div className='text-right'>Actions</div>,
            row: item => getActions(item)
        })
        return tableData;
    }, [props.canPost, props.archived]);


    return <>
        {printData != null ?
            <Dialog title="Lab Results"
                    body={<Print id={printData} />} show={true}
                    setShow={() => setPrintData(null)} />
            : null}

        {resultData != null ?
            <Dialog title="Lab Result"
                    body={<LabResult id={id} 
                                     data={resultData} 
                                     downloadCall={id => SubmissionController.exportResultsSoil({id})}
                                     onExported={() => pagedTableRef.current?.refresh()}
                    />} show={true}
                    setShow={() => setResultData(null)} />
            : null}

        <PagedSearchTable componentRef={pagedTableRef} keyExtractor={r => r.id}
                          call={props.call} columns={tableData}
                          selection={{
                              selectedKeys: selectedSubmissions,
                              onSelectionChange: setSelectedSubmissions
                          }}/>

        <Dialog show={files.length > 0} setShow={() => setFiles([])} title="Files">
            {
                files.map(file => <div key={file.id} className="flex justify-between items-center m-2">
                    <div>{file.name}</div>
                    <a
                        href={"/upload/" + file.path}
                        download
                        className="flex items-center justify-between bg-primary text-white p-2 rounded-lg ml-3 hover:opacity-90"
                    >
                        <div className="mr-2">
                            Download
                        </div>
                        <DownloadSvg/>
                    </a>
                </div>)
            }
        </Dialog>
        <WarningPopup state={warningStateArchive} onYes={v => archive(v)} />
        <WarningPopup state={warningStateRestore} onYes={v => restore(v)} />
        <MFWSuccessDialog data={successData} close={() => setSuccessData(null)} />
    </>
}

export default CompletedResultsSoil;